import { Box, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import outOfUsePattern from 'assets/outOfUsePattern.png';

export type PillColor = 'green' | 'blue' | 'amber' | 'grey' | 'apAvailable' | 'apPending' | 'apRented' | 'apOutOfUse';

interface RowItemPillProps {
	label: string;
	color: PillColor;
	icon?: ReactNode;
	// Fixed-width badge (grows stepwise with the viewport); text truncates to that width.
	fixedWidth?: boolean;
}

export const RowItemPill = ({ label, color, icon, fixedWidth }: RowItemPillProps) => {
	const theme = useTheme();

	// AP out-of-use look: the striped pattern frames a solid grey label chip (matches old Admin.Web 1:1).
	if (color === 'apOutOfUse') {
		return (
			<Box
				component='span'
				title={label}
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					maxWidth: '100%',
					minWidth: 0,
					background: `url(${outOfUsePattern})`,
					borderRadius: '12px',
					padding: '3px',
				}}
			>
				<Box
					component='span'
					sx={{
						backgroundColor: '#F1F1F1',
						color: theme.palette.text.primary,
						borderRadius: '10px',
						padding: '0px 8px',
						fontSize: 11,
						fontWeight: 600,
						lineHeight: 1.4,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{label}
				</Box>
			</Box>
		);
	}

	const textColor: Record<Exclude<PillColor, 'apOutOfUse'>, string> = {
		green: theme.palette.common.white,
		blue: theme.palette.common.white,
		amber: theme.palette.common.white,
		grey: theme.palette.common.white,
		apAvailable: theme.palette.common.white,
		apPending: theme.palette.common.white,
		apRented: theme.palette.common.white,
	};
	const backgroundColor: Record<Exclude<PillColor, 'apOutOfUse'>, string> = {
		green: theme.palette.green.main,
		blue: theme.palette.primary.main,
		amber: theme.palette.secondary.main,
		grey: theme.palette.text.disabled,
		// Match AP (DictAdminMapLockerStatusColor): Available / PendingVerification / Rented.
		apAvailable: '#8EC8FF',
		apPending: '#EB8BED',
		apRented: '#AED87A',
	};

	return (
		<Box
			component='span'
			title={label}
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'flex-start',
				gap: '4px',
				flexGrow: 0,
				flexShrink: 1,
				flexBasis: 'auto',
				width: fixedWidth ? 120 : 'auto',
				minWidth: 0,
				maxWidth: '100%',
				fontSize: 12,
				fontWeight: 600,
				lineHeight: 1.4,
				padding: icon ? '2px 10px 2px 7px' : '2px 10px',
				borderRadius: '12px',
				color: textColor[color],
				background: backgroundColor[color],
				'& svg': { fontSize: 15, flexShrink: 0 },
			}}
		>
			{icon}
			<Box
				component='span'
				sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
			>
				{label}
			</Box>
		</Box>
	);
};
